import { ReactNode, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { Content } from "@/src/components/wrappers/Content";
import { Button } from "@/src/components/ui/button";
import { fetch } from "../../lib/fetch";
import { Enquiry } from "./model";

function fmtDate(d?: string) {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : format(dt, "EEE d MMM yyyy");
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-sm text-slate-800">{children}</span>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-md bg-white border p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}

export function EnquiryDetails() {
  const { id } = useParams();
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch<Enquiry | null>(`/api/enquiries/${id}`)
      .then((data) => setEnquiry(data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Content title="Enquiry">Loading…</Content>;
  if (!enquiry) return <Content title="Enquiry">Enquiry not found.</Content>;

  const c = enquiry.customer;
  const clientName = c?.fullName || [c?.firstName, c?.lastName].filter(Boolean).join(" ") || c?.email || "Enquiry";
  const dates =
    fmtDate(enquiry.checkIn) && fmtDate(enquiry.checkOut)
      ? `${fmtDate(enquiry.checkIn)} – ${fmtDate(enquiry.checkOut)}`
      : fmtDate(enquiry.checkIn) ||
        (enquiry.flexibleDaysRange ? `Flexible · ${enquiry.flexibleDaysRange}` : "Not specified");
  const budget = enquiry.budget
    ? new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: enquiry.currencyCode || "GBP",
        maximumFractionDigits: 0,
      }).format(enquiry.budget)
    : "Not specified";
  const shortlist = enquiry.interestedInOtherEnquiries ?? [];
  const offers = enquiry.offers ?? [];

  return (
    <Content
      title={clientName}
      contentAction={
        <div className="flex gap-2">
          <Button asChild variant="outline" className="bg-white">
            <Link to="/enquiries">← All enquiries</Link>
          </Button>
          {enquiry.hubspotId && enquiry.hubspotId !== "NO_HUBSPOT_ID" && (
            <Button asChild>
              <Link to={`/offers/add/hubspot/${enquiry.hubspotId}`}>Build an offer</Link>
            </Button>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Client">
          <Row label="Name">{clientName}</Row>
          {c?.email && (
            <Row label="Email">
              <a className="text-blue-600" href={`mailto:${c.email}`}>
                {c.email}
              </a>
            </Row>
          )}
          {c?.mobile && <Row label="Phone">{c.mobile}</Row>}
          {enquiry.dateCreated && <Row label="Received">{fmtDate(enquiry.dateCreated)}</Row>}
          <Row label="HubSpot deal">
            <span className="text-slate-500">
              {enquiry.hubspotStatus || "—"} · {enquiry.hubspotId}
            </span>
          </Row>
        </Card>

        <Card title="Trip">
          <Row label="Destination">
            {[enquiry.country, enquiry.charterRegion && enquiry.charterRegion !== enquiry.country ? enquiry.charterRegion : null]
              .filter(Boolean)
              .join(" · ") || "Not specified"}
          </Row>
          <Row label="Dates">{dates}</Row>
          <Row label="Party">
            {enquiry.guests ? `${enquiry.guests} guests` : "Not specified"}
            {enquiry.adults ? ` · ${enquiry.adults} adults` : ""}
            {enquiry.children ? ` · ${enquiry.children} children` : ""}
          </Row>
          <Row label="Charter type">{enquiry.charterType || "Not specified"}</Row>
          {enquiry.yachtType?.length ? <Row label="Boat type">{enquiry.yachtType.join(", ")}</Row> : null}
          {(enquiry.cabinsMin || enquiry.cabinsMax) && (
            <Row label="Cabins">
              {enquiry.cabinsMin ?? "?"}
              {enquiry.cabinsMax && enquiry.cabinsMax !== enquiry.cabinsMin ? `–${enquiry.cabinsMax}` : ""}
            </Row>
          )}
          <Row label="Budget">{budget}</Row>
        </Card>

        {enquiry.vibes?.length ||
        enquiry.activities?.length ||
        enquiry.occasion ||
        enquiry.beenOnYachtingBefore ||
        enquiry.bookingTiming ? (
          <Card title="What they're after">
            {enquiry.occasion ? <Row label="Occasion">{enquiry.occasion}</Row> : null}
            {enquiry.vibes?.length ? <Row label="Vibe">{enquiry.vibes.join(", ")}</Row> : null}
            {enquiry.activities?.length ? <Row label="Activities">{enquiry.activities.join(", ")}</Row> : null}
            {enquiry.bookingTiming ? <Row label="Booking timing">{enquiry.bookingTiming}</Row> : null}
            {enquiry.beenOnYachtingBefore ? (
              <Row label="Chartered before">{enquiry.beenOnYachtingBefore}</Row>
            ) : null}
          </Card>
        ) : null}

        {enquiry.additionalMessage ? (
          <Card title="Message">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{enquiry.additionalMessage}</p>
          </Card>
        ) : null}

        {enquiry.extras?.length ? (
          <Card title="Requested extras">
            <ul className="list-disc pl-5 text-sm text-slate-700">
              {enquiry.extras.map((x, i) => (
                <li key={i}>
                  {x.name}
                  {x.comment ? ` — ${x.comment}` : ""}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card title={`Shortlisted yachts${shortlist.length ? ` (${shortlist.length})` : ""}`}>
          {shortlist.length ? (
            <ul className="flex flex-col gap-1.5">
              {shortlist.map((y, i) => (
                <li key={i} className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-medium text-slate-800">{y.yachtName || "Yacht"}</span>
                  <span className="text-slate-500">{y.location}</span>
                  {y.yachtUrl ? (
                    <a className="text-blue-600" href={y.yachtUrl} target="_blank" rel="noreferrer">
                      View
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">No yachts shortlisted in the builder.</p>
          )}
        </Card>

        <Card title={`Offers${offers.length ? ` (${offers.length})` : ""}`}>
          {offers.length ? (
            <ul className="flex flex-col gap-1.5">
              {offers.map((o) => (
                <li key={o.id}>
                  <Link className="text-blue-600 text-sm" to={`/offers/${o.id}`}>
                    Offer {o.id} {o.status ? `· ${o.status}` : ""}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-400">No offer built yet.</p>
          )}
        </Card>
      </div>
    </Content>
  );
}
